import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { ref, onValue } from 'firebase/database';
import { db } from 'utils/firebase-config';
import { api } from 'store/services/general.services';
import CustomModal from 'components/customModal/CustomModal';
import adminMessageIcon from 'assets/svg/admin-message-icon.svg';
import infoIcon from 'assets/svg/admin-message-info.svg';
import closeModalIcon from 'assets/svg/close-icon-in-modal.svg';
import Styles from './AdminMessages.module.scss';

interface AdminMessage {
  pk: number;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

interface AdminMessagesProps {
  /** Marketing Home: public broadcasts, no mark-read / unread badges. */
  publicMode?: boolean;
}

const AdminMessages: React.FC<AdminMessagesProps> = ({ publicMode = false }) => {
  const [messages, setMessages] = useState<AdminMessage[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [expandedPk, setExpandedPk] = useState<number | null>(null);
  const expandedItemRef = useRef<HTMLDivElement | null>(null);

  const loadMessages = useCallback(() => {
    const request = publicMode
      ? api.publicAdminBroadcastsRequest()
      : api.adminMessagesRequest();
    request
      .then((res: any) => {
        if (Array.isArray(res)) {
          setMessages(res);
        }
      })
      .catch(() => {});
  }, [publicMode]);

  // Initial load
  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Real-time: the backend bumps `adminNotifications/lastSentAt` whenever a new
  // admin broadcast is sent, so refetch on every change (no page refresh).
  useEffect(() => {
    const signalRef = ref(db, 'adminNotifications/lastSentAt');
    let first = true;
    const unsubscribe = onValue(signalRef, () => {
      // onValue fires once immediately on subscribe — skip that initial value
      // since the mount effect above already loaded the messages.
      if (first) {
        first = false;
        return;
      }
      loadMessages();
    });
    return () => unsubscribe();
  }, [loadMessages]);

  const unreadCount = publicMode ? 0 : messages.filter(m => !m.is_read).length;

  const markRead = (msg: AdminMessage) => {
    if (publicMode || msg.is_read) return;
    setMessages(prev => prev.map(m => (m.pk === msg.pk ? { ...m, is_read: true } : m)));
    api.markNotificationReadRequest({ pk: msg.pk }).catch(() => {});
  };

  // Open the inbox modal with the clicked message expanded (and marked read).
  const openModal = (msg: AdminMessage) => {
    setExpandedPk(msg.pk);
    markRead(msg);
    setModalOpen(true);
    document.body.classList.add('modal-open');
  };

  const closeModal = () => {
    setModalOpen(false);
    document.body.classList.remove('modal-open');
  };

  // Single-expand accordion: open one collapses the rest; expanding marks read.
  const toggleExpand = (msg: AdminMessage) => {
    const willExpand = expandedPk !== msg.pk;
    setExpandedPk(willExpand ? msg.pk : null);
    if (willExpand) markRead(msg);
  };

  // Bring the initially-expanded message into view when the modal opens.
  useEffect(() => {
    if (modalOpen && expandedItemRef.current) {
      expandedItemRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [modalOpen]);

  return (
    <div className={Styles.wrapper}>
      <div className={Styles.header}>
        <img src={adminMessageIcon} alt="" className={Styles.headerIcon} />
        <span>ADMIN MESSAGES</span>
        {unreadCount > 0 && <span className={Styles.badge}>{unreadCount}</span>}
      </div>

      <div className={Styles.listViewport}>
        <div className={`${Styles.messageList} customScrollBar`}>
          {messages.length === 0 && <p className={Styles.empty}>No messages yet.</p>}
          {messages.map(msg => (
            <div
              className={`${Styles.messageItem} ${msg.is_read || publicMode ? '' : Styles.unread}`}
              key={msg.pk}
              onClick={() => openModal(msg)}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') openModal(msg);
              }}
            >
              <div className={Styles.titleRow}>
                <div className={Styles.infoIconWrap}>
                  {!msg.is_read && !publicMode && <span className={Styles.unreadDot} />}
                  <img src={infoIcon} alt="" className={Styles.infoIcon} />
                </div>
                {msg.title && <p className={Styles.title}>{msg.title}</p>}
              </div>
              {msg.message && <p className={Styles.body}>{msg.message}</p>}
              {msg.created_at && <p className={Styles.time}>{moment(msg.created_at).fromNow()}</p>}
            </div>
          ))}
        </div>
      </div>

      {modalOpen && (
        <CustomModal closeDialog={closeModal} wrapperClass={Styles.inboxModal} cssClass={`customScrollBar ${Styles.inboxBody}`}>
          <div className={`${Styles.header} ${Styles.modalHeaderBar}`}>
            <img src={adminMessageIcon} alt="" className={Styles.headerIcon} />
            <span>ADMIN MESSAGES</span>
            {unreadCount > 0 && <span className={Styles.badge}>{unreadCount}</span>}
          </div>
          <div className={Styles.inboxList}>
            {messages.length === 0 && <p className={Styles.empty}>No messages yet.</p>}
            {messages.map(msg => {
              const expanded = expandedPk === msg.pk;
              return (
                <div
                  key={msg.pk}
                  ref={expanded ? expandedItemRef : undefined}
                  className={`${Styles.messageItem} ${msg.is_read || publicMode ? '' : Styles.unread}`}
                >
                  <button type="button" className={Styles.accHeader} onClick={() => toggleExpand(msg)}>
                    <div className={Styles.infoIconWrap}>
                      {!msg.is_read && !publicMode && <span className={Styles.unreadDot} />}
                      <img src={infoIcon} alt="" className={Styles.infoIcon} />
                    </div>
                    <p className={Styles.title}>{msg.title}</p>
                    {msg.created_at && (
                      <span className={Styles.accTime}>{moment(msg.created_at).fromNow()}</span>
                    )}
                    <svg
                      className={`${Styles.chevron} ${expanded ? Styles.chevronOpen : ''}`}
                      width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  {expanded && (
                    <div className={Styles.accBody}>
                      {msg.message && <p className={Styles.detailBody}>{msg.message}</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <button type="button" className={Styles.detailCloseBtn} onClick={closeModal}>
            Close
            <img src={closeModalIcon} alt="" className={Styles.detailCloseBtnIcon} />
          </button>
        </CustomModal>
      )}
    </div>
  );
};

export default AdminMessages;
